import { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { authService } from '@/services/auth/authService';

/**
 * Shown when the session has ended and could not be renewed — D7 bounds it twice, by
 * inactivity and by absolute length, so this is now an ordinary daily event rather than
 * an edge case.
 *
 * Deliberately not ConfirmDialog: there is no second option to weigh. The session is
 * already gone, and the only question is when the user is ready to be sent to the login
 * page — which is why the dialog cannot be dismissed by clicking away, and why it names
 * the consequence for unsaved input instead of pretending there is none. Signing in
 * returns to the current view; what was typed into an open form does not survive the
 * round trip, and saying so is the point of asking at all.
 */
export const SessionExpiredDialog = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  useEffect(() => authService.onSessionEnded(() => setOpen(true)), []);

  const signIn = () => {
    setOpen(false);
    // The current view is the return URL, so the user lands where they left off.
    void authService.beginInteractiveLogin(undefined, window.location.pathname + window.location.search);
  };

  return (
    <Dialog open={open} maxWidth="xs" fullWidth disableEscapeKeyDown>
      <DialogTitle>
        {t('session.expiredTitle', { defaultValue: 'Your session has ended' })}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t('session.expiredMessage', {
            defaultValue:
              'You have been signed out. Sign in again to continue — you will return to this page, but anything typed into an open form since the last save is not kept.',
          })}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={signIn} variant="contained" color="primary" autoFocus>
          {t('session.signInAgain', { defaultValue: 'Sign in again' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
